"use client";

import { FileIcon, FileTextIcon } from "icons";
import React, { useEffect, useRef, useState } from "react";
import { Heading, Modal, Button, Text, ImageWrapper } from "ui-components";

import { makeToast } from "@/src/components/Toast/Toast";
import {
  CreateAssetDtoFileType,
  useUserUpdateProfile,
  useUserLogActivity,
} from "@/src/hooks/api/generated";
import { useAuth } from "@/src/hooks/useAuth";
import { useUploadAssets } from "@/src/hooks/useUploadAssets";
import { changeBrazeUser, logBrazeCustomEvent } from "@/utils/brazeClient";

export function UserCVUploadPrompt(): JSX.Element | null {
  const { authData } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<"intro" | "upload" | "success">("intro");
  const [file, setFile] = useState<File | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: uploadCV, status: uploadStatus } = useUploadAssets();
  const { mutate: updateProfile, status: updateStatus } =
    useUserUpdateProfile();
  const { mutate: logActivity } = useUserLogActivity();

  const isPending = uploadStatus === "pending" || updateStatus === "pending";

  useEffect(() => {
    if (!authData) {
      return;
    }

    // Check if user already has a CV uploaded
    if (authData.userTaxonomy?.pdf) {
      return;
    }

    // Check if prompt was dismissed within the last 30 days
    const dismissalStorageKey = `cv_prompt_dismissed_${authData.id}`;
    const dismissedAtRaw = localStorage.getItem(dismissalStorageKey);
    let shouldShow = true;

    if (dismissedAtRaw) {
      const dismissedAt = new Date(dismissedAtRaw);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - dismissedAt.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 30) {
        shouldShow = false;
      }
    }

    if (shouldShow) {
      // Check if shown in this session to prevent spamming on reload
      const sessionShownKey = `cv_prompt_shown_${authData.id}`;
      if (!sessionStorage.getItem(sessionShownKey)) {
        setIsOpen(true);
        sessionStorage.setItem(sessionShownKey, "true");
        logActivity({ data: { type: "CV upload prompt displayed" } });
      }
    }
  }, [authData, logActivity]);

  const handleDismiss = (): void => {
    if (authData?.id) {
      const dismissalStorageKey = `cv_prompt_dismissed_${authData.id}`;
      localStorage.setItem(dismissalStorageKey, new Date().toISOString());
      logActivity({ data: { type: "CV upload prompt dismissed" } });
    }
    setIsOpen(false);
  };

  const handleFinish = (pdfId?: number): void => {
    if (pdfId) {
      updateProfile(
        { data: { pdfId } },
        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSuccess: (userData: any) => {
            changeBrazeUser(userData);
            logBrazeCustomEvent("MEMBER_RESUME", {
              id: authData?.id,
              resume_date: new Date().toISOString(),
            });
            // According to task tracking instructions
            logActivity({ data: { type: "CV uploaded from prompt" } });

            setStep("success");
          },
        }
      );
    }
  };

  const handleSelectFile = (selectedFile: File): void => {
    if (selectedFile.size > 5 * 1024 * 1024) {
      makeToast({
        title: "File is too large. Maximum file size is 5MB.",
        intent: "danger",
      });
      return;
    }
    setFile(selectedFile);
  };

  const handleClick = (): void => {
    if (!file) {
      makeToast({ title: "Please select a CV", intent: "danger" });
      return;
    }

    uploadCV(
      {
        data: {
          type: "resume",
          entity_type: "users",
          entity_id: authData?.id?.toString() || "",
          file,
          original_name: file.name,
          fileType: file.type as CreateAssetDtoFileType,
        },
      },
      {
        onSuccess: (uploadData) => {
          handleFinish(uploadData.id);
        },
      }
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(val) => {
        if (!val) {
          if (step !== "success") {
            handleDismiss();
          } else {
            setIsOpen(false);
          }
        }
      }}
      closeIcons
      isDismissable
      animatedModalClassName="md:min-w-[700px] flex flex-1 items-center justify-center min-h-[400px]"
    >
      <div className="flex flex-col items-center justify-start gap-6 pt-5 w-full">
        {step === "intro" && (
          <div className="flex flex-col items-center justify-center gap-4 py-4 w-full max-w-[37rem] px-4">
            <div className="mb-2">
              <ImageWrapper
                src="https://res.cloudinary.com/dv6hrcluu/image/upload/v1773834283/web-app-images/get_discovered_by_inclusive_employers_sn7idc.png"
                width={200}
                height={127}
              />
            </div>
            <Heading align="center">
              Get discovered by inclusive employers
            </Heading>
            <Text align="center" className="mt-2 text-[15px] leading-relaxed">
              Seems you haven&apos;t uploaded your CV yet &mdash; and top
              inclusive employers are actively looking for talent like you.
            </Text>
            <Text align="center" className="mb-4 text-[15px] leading-relaxed">
              It takes just a minute and lets them find you &mdash; in a space
              where you can show up as your whole self.
            </Text>
            <div className="flex flex-col items-center gap-4 w-full mt-2">
              <Button
                onClick={() => setStep("upload")}
                size="lg"
                className="w-48"
              >
                Upload CV
              </Button>
              <button
                className="text-sm font-medium underline mt-1"
                type="button"
                onClick={handleDismiss}
              >
                Remind me later
              </button>
            </div>
          </div>
        )}

        {step === "upload" && (
          <>
            <Heading align="center">Upload your CV</Heading>

            <div className="w-full max-w-lg flex flex-col gap-2 mt-4 px-4">
              <div
                className="w-full border-2 border-dashed border-background-200 rounded-md p-10 flex flex-col items-center justify-center cursor-pointer hover:bg-background-200/20 transition-colors bg-white min-h-[160px]"
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.click();
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                    handleSelectFile(e.dataTransfer.files[0]);
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    inputRef.current?.click();
                  }
                }}
              >
                {file ? (
                  <div className="flex flex-col md:flex-row items-center justify-between w-full">
                    <div className="flex flex-row items-center gap-2">
                      <FileTextIcon className="text-brand-primary" />
                      <Text className="!text-sm font-medium">{file.name}</Text>
                    </div>
                    <div className="flex flex-row items-center gap-6 mt-4 md:mt-0">
                      <button
                        type="button"
                        className="text-sm font-medium underline transition-colors hover:text-brand-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          inputRef.current?.click();
                        }}
                      >
                        Replace file
                      </button>
                      <button
                        type="button"
                        className="text-sm font-medium underline transition-colors hover:text-brand-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(undefined);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center pointer-events-none">
                    <FileIcon className="mx-auto h-12 w-12 opacity-50 mb-4" />
                    <Text className="text-center !text-sm">
                      <span className="text-brand-primary font-medium">
                        Upload a CV
                      </span>{" "}
                      or drag and drop
                    </Text>
                  </div>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="sr-only"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleSelectFile(e.target.files[0]);
                    }
                  }}
                />
              </div>

              <Text className="!text-left !text-sm text-gray-500">
                Accepted file types: PDF, DOC, DOCX · Max 5MB
              </Text>
            </div>

            <div className="flex flex-col items-center gap-4 w-full mt-6">
              <Button
                onClick={handleClick}
                isDisabled={isPending}
                isLoading={isPending}
                size="lg"
                className="w-48"
              >
                Upload & Finish
              </Button>
              <button
                className="text-sm font-medium underline mt-2"
                type="button"
                onClick={() => setStep("intro")}
                disabled={isPending}
              >
                Back
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <div className="flex flex-col items-center justify-center gap-4 py-8 w-full max-w-sm px-4">
            <div className="mb-4">
              <ImageWrapper
                src="https://res.cloudinary.com/dv6hrcluu/image/upload/v1773836471/web-app-images/you_are_all_set_q4dgzf.png"
                width={140}
                height={164}
              />
            </div>
            <Heading align="center">You&apos;re all set!</Heading>
            <Text align="center" className="my-2">
              Your CV is now visible to inclusive employers actively searching
              for talent like you.
            </Text>
            <div className="flex flex-col items-center w-full mt-4">
              <Button
                onClick={() => {
                  setIsOpen(false);
                }}
                size="lg"
                className="w-48"
              >
                Back To My Homepage
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
